import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import * as yup from "yup"
import { UpdateCompanyDto } from "~/ui-rtk/api/companyApi"

import { hasErrorMessage } from "~/ui-rtk/utils/http-utils"
import { CompanySetupStatus } from "~/ui-rtk/api/types"
import useCompanyUpdater from "~/ui-rtk/hooks/company-updater"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  buildKeywordsMetadata,
  mergeAliasIntoBrands,
  resolveOwnBrandTerm,
} from "~/ui-rtk/utils/keywords"
import {
  SOS_FREE_MAX_CATEGORIES,
  SOS_FREE_MAX_COMPETITORS,
} from "~/ui-rtk/constants/sos"

// The gks_v2 connector writes NO data unless BOTH categories and
// competitors are configured — categories/competitors are required here.
// Free tier tracks exactly ONE brand term (the primary keyword); the
// Keywords & Competitors settings surface edits the same single term.
export const validationSchema = yup.object().shape({
  primaryBrandKeyword: yup
    .string()
    .required("Primary brand keyword is required"),
  categoryKeywords: yup
    .array()
    .of(yup.string())
    .min(1, "Add at least one category — your report needs it")
    .max(
      SOS_FREE_MAX_CATEGORIES,
      `Up to ${SOS_FREE_MAX_CATEGORIES} categories on the free plan`,
    ),
  competitorsKeywords: yup
    .array()
    .of(yup.string())
    .min(1, "Add at least one competitor — your report needs it")
    .max(
      SOS_FREE_MAX_COMPETITORS,
      `Up to ${SOS_FREE_MAX_COMPETITORS} competitors on the free plan`,
    ),
})

export type SosKeywordsFormValues = yup.InferType<typeof validationSchema>

export const useConnect = () => {
  const currentCompany = useCurrentCompany()
  const { keywords } = (currentCompany?.metadata ?? {}) as {
    keywords: Record<string, Record<string, unknown>>
  }

  const { updateCompany, inProgress: isSubmitting } = useCompanyUpdater()

  const initialValues: SosKeywordsFormValues = useMemo(
    () => ({
      primaryBrandKeyword: (keywords?.google?.searchAlias ??
        currentCompany?.name ??
        "") as string,
      competitorsKeywords: (keywords?.google?.competitors ?? []) as string[],
      categoryKeywords: (keywords?.google?.categories ?? []) as string[],
    }),
    [currentCompany],
  )

  const submit = useCallback(
    async (values: SosKeywordsFormValues) => {
      if (!currentCompany) {
        return
      }

      const updateCompanyDto: UpdateCompanyDto = {
        // Deliberately COMPLETE at keywords-save even though the SoS pipeline
        // hasn't produced data yet: the SOS flow has no connector onboarding
        // to finish, and the data-ready fall-through in Auth (APP-449) relies
        // on isSetupStatusComplete to reach Main once the report lands. For a
        // SOS_FREE company, "provisioned" must be read from tier +
        // metadata.sosReportReadyNotifiedAt — never from setupStatus alone.
        setupStatus: CompanySetupStatus.COMPLETE,
        metadata: buildKeywordsMetadata(currentCompany, {
          searchAlias: values.primaryBrandKeyword,
          // Free tier: brands[] is exactly the one brand term — dbt drops the
          // own-brand rows unless the alias appears there. Any extra brands
          // from before the single-keyword policy are deliberately dropped.
          brands: mergeAliasIntoBrands(
            resolveOwnBrandTerm(
              values.primaryBrandKeyword,
              currentCompany.name,
            ),
            [],
          ),
          competitors: values.competitorsKeywords as string[],
          categories: values.categoryKeywords as string[],
          // instagramCompetitors omitted: the SoS wizard has no IG field,
          // so existing handles are preserved.
        }),
      }

      try {
        const updated = await updateCompany(updateCompanyDto)
        if (updated) {
          toast.success("You're all set — we're building your report", {
            toastId: "sos-keywords-update-success",
          })
          // No navigation: SosOnboarding re-renders from the refetched
          // session and switches to its confirmation state.
        }
      } catch (error) {
        if (hasErrorMessage(error)) {
          toast.error(error.data.message, {
            toastId: "sos-keywords-update-error",
          })
        }
      }
    },
    [currentCompany],
  )

  return {
    isSubmitting,
    initialValues,
    validationSchema,
    updateKeywords: submit,
  }
}
